import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AutomationRule, TaskStatus, TaskPriority, Phase, Sprint } from '../../types';
import {
  getProjectRules, toggleRule, deleteRule, createRule, updateRule,
  TriggerType, TRIGGER_LABEL, ActionType, ACTION_LABEL, RULE_LIMIT, RulePayload,
} from '../../services/automationService';
import { getProjectPhases } from '../../services/milestoneService';
import { getProjectSprints, SprintStatus } from '../../services/sprintService';
import { graphGet } from '../../api/graphClient';
import PeoplePicker, { Person } from '../PeoplePicker';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../BoardModal';

const STATUS_LABEL: Record<number, string> = {
  [TaskStatus.NotStarted]: 'Not Started', [TaskStatus.InProgress]: 'In Progress', [TaskStatus.InReview]: 'In Review',
  [TaskStatus.Done]: 'Done', [TaskStatus.OnHold]: 'On Hold', [TaskStatus.Cancelled]: 'Cancelled',
};
const STATUS_VALUES = [TaskStatus.NotStarted, TaskStatus.InProgress, TaskStatus.InReview, TaskStatus.Done, TaskStatus.OnHold, TaskStatus.Cancelled];
const PRIORITY_LABEL: Record<number, string> = {
  [TaskPriority.Low]: 'Low', [TaskPriority.Medium]: 'Medium', [TaskPriority.High]: 'High', [TaskPriority.Critical]: 'Critical',
};
const PRIORITY_VALUES = [TaskPriority.Low, TaskPriority.Medium, TaskPriority.High, TaskPriority.Critical];

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// Action label for the rule list. Post-to-channel rules show "Team › Channel".
function actionSummary(r: AutomationRule): string {
  const label = ACTION_LABEL[r.dagrun_pm_actiontype] ?? '—';
  if (r.dagrun_pm_actiontype === ActionType.PostToChannel && r.dagrun_pm_actionparamsjson) {
    try {
      const p = JSON.parse(r.dagrun_pm_actionparamsjson);
      if (p.teamName || p.channelName) return `${p.teamName ?? 'Team'} › ${p.channelName ?? 'Channel'}`;
    } catch { /* fall through to label */ }
  }
  return label;
}

export default function AutomationRulesSection({ pca, projectId }: { pca: IPublicClientApplication; projectId: string }) {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AutomationRule | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AutomationRule | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    return getProjectRules(pca, projectId).then(setRules).finally(() => setLoading(false));
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const atLimit = rules.length >= RULE_LIMIT;

  const onToggle = async (r: AutomationRule) => {
    const next = !r.dagrun_pm_isenabled;
    setRules(prev => prev.map(x => x.dagrun_pm_automationruleid === r.dagrun_pm_automationruleid ? { ...x, dagrun_pm_isenabled: next } : x));
    try { await toggleRule(pca, r.dagrun_pm_automationruleid, next); } catch { reload(); }
  };

  return (
    <div className="card" style={{ padding: 20, marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="eyebrow">AUTOMATION RULES</span>
        {atLimit ? (
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>Rule limit reached ({RULE_LIMIT}/{RULE_LIMIT})</span>
        ) : (
          <button type="button" className="btn-secondary" onClick={() => setEditing('new')}>+ New Rule</button>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
      ) : rules.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 0' }}>No automation rules — create your first rule.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <Th>On</Th><Th>Name</Th><Th>When</Th><Th>Then</Th><Th>Last Run</Th><Th>Count</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.dagrun_pm_automationruleid} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                <Td><input type="checkbox" checked={r.dagrun_pm_isenabled} onChange={() => onToggle(r)} /></Td>
                <Td><span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{r.dagrun_pm_name}</span></Td>
                <Td><span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{TRIGGER_LABEL[r.dagrun_pm_triggertype] ?? '—'}</span></Td>
                <Td><span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{actionSummary(r)}</span></Td>
                <Td><span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{fmtDate(r.dagrun_pm_lasttriggeredon)}</span></Td>
                <Td><span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{r.dagrun_pm_triggercount ?? 0}</span></Td>
                <Td>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <LinkBtn onClick={() => setEditing(r)}>Edit</LinkBtn>
                    <LinkBtn danger onClick={() => setConfirmDelete(r)}>Delete</LinkBtn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <RuleBuilder pca={pca} projectId={projectId}
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}

      {confirmDelete && (
        <BoardModal open title="Delete rule" onClose={() => setConfirmDelete(null)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(null)} />
            <BMDangerButton onClick={() => deleteRule(pca, confirmDelete.dagrun_pm_automationruleid).then(() => { setConfirmDelete(null); reload(); })}>Delete</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{confirmDelete.dagrun_pm_name}</strong>? This cannot be undone.
        </BoardModal>
      )}
    </div>
  );
}

// ── Rule builder drawer ───────────────────────────────────────────────

function parseJson(json?: string | null): Record<string, unknown> {
  if (!json) return {};
  try { const p = JSON.parse(json); return p && typeof p === 'object' ? p : {}; } catch { return {}; }
}

function RuleBuilder({ pca, projectId, rule, onClose, onSaved }: {
  pca: IPublicClientApplication; projectId: string; rule: AutomationRule | null;
  onClose: () => void; onSaved: () => void;
}) {
  const tp0 = parseJson(rule?.dagrun_pm_triggerparamsjson);
  const ap0 = parseJson(rule?.dagrun_pm_actionparamsjson);
  const cond0 = rule?.dagrun_pm_conditionjson ? parseJson(rule.dagrun_pm_conditionjson) : null;

  const [name, setName] = useState(rule?.dagrun_pm_name ?? '');
  const [triggerType, setTriggerType] = useState<number>(rule?.dagrun_pm_triggertype ?? TriggerType.TaskStatusChanged);
  const [triggerParams, setTriggerParams] = useState<Record<string, unknown>>(tp0);
  const [actionType, setActionType] = useState<number>(rule?.dagrun_pm_actiontype ?? ActionType.ChangeStatus);
  const [actionParams, setActionParams] = useState<Record<string, unknown>>(ap0);
  const [assignee, setAssignee] = useState<Person | null>(
    ap0.assigneeId ? { aadObjectId: String(ap0.assigneeId), displayName: String(ap0.assigneeName ?? ap0.assigneeId) } : null);
  const [notifyPerson, setNotifyPerson] = useState<Person | null>(
    ap0.userId ? { aadObjectId: String(ap0.userId), displayName: String(ap0.userName ?? ap0.userId) } : null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [condOn, setCondOn] = useState<boolean>(!!cond0);
  const [condField, setCondField] = useState<string>((cond0?.field as string) ?? 'priority');
  const [condOp, setCondOp] = useState<string>((cond0?.operator as string) ?? 'equals');
  const [condValue, setCondValue] = useState<string>((cond0?.value as string) ?? '');
  const [enabled, setEnabled] = useState<boolean>(rule?.dagrun_pm_isenabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load phases + sprints for the Move-to-phase / Move-to-sprint dropdowns.
  useEffect(() => {
    getProjectPhases(pca, projectId).then(setPhases).catch(() => {});
    getProjectSprints(pca, projectId).then(setSprints).catch(() => {});
  }, [pca, projectId]);

  const setTP = (k: string, v: unknown) => setTriggerParams(p => ({ ...p, [k]: v }));
  const setAP = (k: string, v: unknown) => setActionParams(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const payload: RulePayload = {
        name: name.trim(),
        triggerType, triggerParams,
        condition: condOn ? { field: condField, operator: condOp, value: condValue } : null,
        actionType, actionParams,
        isEnabled: enabled,
      };
      if (rule) await updateRule(pca, rule.dagrun_pm_automationruleid, payload);
      else await createRule(pca, projectId, payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title={rule ? 'Edit Rule' : 'New Rule'} width={560} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Saving…' : 'Save Rule'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 16 }}>
        <label style={BM_LABEL}>Rule Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} />
      </div>

      {/* WHEN */}
      <FieldGroup tag="WHEN" color="#0E7490">
        <select value={triggerType} onChange={e => { setTriggerType(Number(e.target.value)); setTriggerParams({}); }} style={BM_INPUT}>
          {Object.entries(TRIGGER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div style={{ marginTop: 8 }}>
          {triggerType === TriggerType.TaskStatusChanged && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Labeled label="From status">
                <select value={String(triggerParams.fromStatus ?? '')} onChange={e => setTP('fromStatus', e.target.value === '' ? null : Number(e.target.value))} style={BM_INPUT}>
                  <option value="">Any</option>
                  {STATUS_VALUES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </Labeled>
              <Labeled label="To status">
                <select value={String(triggerParams.toStatus ?? '')} onChange={e => setTP('toStatus', e.target.value === '' ? null : Number(e.target.value))} style={BM_INPUT}>
                  <option value="">Any</option>
                  {STATUS_VALUES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </Labeled>
            </div>
          )}
          {triggerType === TriggerType.TaskDueDateArrived && (
            <Labeled label="Days before due">
              <input type="number" min={0} value={Number(triggerParams.daysBefore ?? 1)} onChange={e => setTP('daysBefore', Math.max(0, Number(e.target.value) || 0))} style={BM_INPUT} />
            </Labeled>
          )}
          {triggerType === TriggerType.TaskNoUpdateForNDays && (
            <Labeled label="Days without update">
              <input type="number" min={1} value={Number(triggerParams.days ?? 3)} onChange={e => setTP('days', Math.max(1, Number(e.target.value) || 1))} style={BM_INPUT} />
            </Labeled>
          )}
          {triggerType === TriggerType.CustomFieldChanged && (
            <Labeled label="Field logical name">
              <input value={String(triggerParams.field ?? '')} onChange={e => setTP('field', e.target.value)} style={BM_INPUT} placeholder="dagrun_pm_…" />
            </Labeled>
          )}
        </div>
      </FieldGroup>

      {/* IF (optional) */}
      <FieldGroup tag="IF" color="#92400E">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', marginBottom: condOn ? 8 : 0 }}>
          <input type="checkbox" checked={condOn} onChange={e => setCondOn(e.target.checked)} /> Add condition
        </label>
        {condOn && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <select value={condField} onChange={e => setCondField(e.target.value)} style={BM_INPUT}>
              <option value="assignee">Assignee</option>
              <option value="priority">Priority</option>
              <option value="status">Status</option>
              <option value="collection">Collection</option>
              <option value="label">Label</option>
            </select>
            <select value={condOp} onChange={e => setCondOp(e.target.value)} style={BM_INPUT}>
              <option value="equals">equals</option>
              <option value="notEquals">not equals</option>
              <option value="contains">contains</option>
            </select>
            <input value={condValue} onChange={e => setCondValue(e.target.value)} style={{ ...BM_INPUT, gridColumn: '1 / span 2' }} placeholder="Value" />
          </div>
        )}
      </FieldGroup>

      {/* THEN */}
      <FieldGroup tag="THEN" color="#065F46">
        <select value={actionType} onChange={e => { setActionType(Number(e.target.value)); setActionParams({}); setAssignee(null); setNotifyPerson(null); }} style={BM_INPUT}>
          {Object.entries(ACTION_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div style={{ marginTop: 8 }}>
          {actionType === ActionType.AssignTo && (
            <Labeled label="Assign to">
              <PeoplePicker pca={pca} value={assignee} onChange={p => { setAssignee(p); setActionParams(ap => ({ ...ap, assigneeId: p?.aadObjectId ?? null, assigneeName: p?.displayName ?? null })); }} placeholder="Pick a person…" />
            </Labeled>
          )}
          {actionType === ActionType.ChangeStatus && (
            <Labeled label="New status">
              <select value={String(actionParams.status ?? STATUS_VALUES[0])} onChange={e => setAP('status', Number(e.target.value))} style={BM_INPUT}>
                {STATUS_VALUES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </Labeled>
          )}
          {actionType === ActionType.SetPriority && (
            <Labeled label="New priority">
              <select value={String(actionParams.priority ?? PRIORITY_VALUES[0])} onChange={e => setAP('priority', Number(e.target.value))} style={BM_INPUT}>
                {PRIORITY_VALUES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
              </select>
            </Labeled>
          )}
          {actionType === ActionType.SetDueDate && (
            <Labeled label="Days from trigger">
              <input type="number" value={Number(actionParams.daysFromTrigger ?? 0)} onChange={e => setAP('daysFromTrigger', Number(e.target.value) || 0)} style={BM_INPUT} />
            </Labeled>
          )}
          {actionType === ActionType.AddLabel && (
            <Labeled label="Label"><input value={String(actionParams.label ?? '')} onChange={e => setAP('label', e.target.value)} style={BM_INPUT} /></Labeled>
          )}
          {actionType === ActionType.CreateFollowupTask && (
            <Labeled label="Follow-up task title"><input value={String(actionParams.title ?? '')} onChange={e => setAP('title', e.target.value)} style={BM_INPUT} /></Labeled>
          )}
          {actionType === ActionType.NotifyUser && (
            <Labeled label="Notify">
              <PeoplePicker pca={pca} value={notifyPerson} onChange={p => { setNotifyPerson(p); setActionParams(ap => ({ ...ap, userId: p?.aadObjectId ?? null, userName: p?.displayName ?? null })); }} placeholder="Pick a person…" />
            </Labeled>
          )}
          {actionType === ActionType.PostToChannel && (
            <TeamsChannelPicker pca={pca}
              teamId={(actionParams.teamId as string) ?? ''}
              channelId={(actionParams.channelId as string) ?? ''}
              onChange={v => setActionParams(ap => ({ ...ap, ...v }))} />
          )}
          {actionType === ActionType.SendEmail && (
            <Labeled label="Recipient email address"><input type="email" value={String(actionParams.email ?? '')} onChange={e => setAP('email', e.target.value)} style={BM_INPUT} placeholder="name@example.com" /></Labeled>
          )}
          {actionType === ActionType.MoveToPhase && (
            <Labeled label="Phase">
              <select value={String(actionParams.phaseId ?? '')} onChange={e => { const id = e.target.value; setActionParams(ap => ({ ...ap, phaseId: id || null, phaseName: phases.find(p => p.dagrun_pm_phaseid === id)?.dagrun_pm_name ?? null })); }} style={BM_INPUT}>
                <option value="">Select a phase…</option>
                {phases.map(p => <option key={p.dagrun_pm_phaseid} value={p.dagrun_pm_phaseid}>{p.dagrun_pm_name}</option>)}
              </select>
            </Labeled>
          )}
          {actionType === ActionType.MoveToSprint && (
            <Labeled label="Sprint">
              <select value={String(actionParams.sprintId ?? '')} onChange={e => { const id = e.target.value; setActionParams(ap => ({ ...ap, sprintId: id || null, sprintName: sprints.find(s => s.dagrun_pm_sprintid === id)?.dagrun_pm_name ?? null })); }} style={BM_INPUT}>
                <option value="">Select a sprint…</option>
                {sprints
                  .filter(s => s.dagrun_pm_status === SprintStatus.Planned || s.dagrun_pm_status === SprintStatus.Active)
                  .map(s => <option key={s.dagrun_pm_sprintid} value={s.dagrun_pm_sprintid}>{s.dagrun_pm_name}</option>)}
              </select>
            </Labeled>
          )}
        </div>
      </FieldGroup>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', marginTop: 6 }}>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Enabled
      </label>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// Two-step Teams → Channel picker. Loads the user's joined teams, then the
// selected team's channels, storing teamId/teamName/channelId/channelName.
interface GraphNamed { id: string; displayName: string }
function TeamsChannelPicker({ pca, teamId, channelId, onChange }: {
  pca: IPublicClientApplication; teamId: string; channelId: string;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const [teams, setTeams] = useState<GraphNamed[]>([]);
  const [channels, setChannels] = useState<GraphNamed[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    graphGet<{ value: GraphNamed[] }>(pca, '/me/joinedTeams?$select=id,displayName')
      .then(d => { if (!cancelled) setTeams(d.value ?? []); })
      .catch((e: Error) => {
        console.error('TeamsChannelPicker: GET /me/joinedTeams failed', e);
        if (!cancelled) setError(e.message);
      })
      .finally(() => { if (!cancelled) setLoadingTeams(false); });
    return () => { cancelled = true; };
  }, [pca]);

  useEffect(() => {
    if (!teamId) { setChannels([]); return; }
    let cancelled = false;
    setLoadingChannels(true);
    graphGet<{ value: GraphNamed[] }>(pca, `/teams/${teamId}/channels?$select=id,displayName`)
      .then(d => { if (!cancelled) setChannels(d.value ?? []); })
      .catch(() => { if (!cancelled) setChannels([]); })
      .finally(() => { if (!cancelled) setLoadingChannels(false); });
    return () => { cancelled = true; };
  }, [pca, teamId]);

  return (
    <>
      <Labeled label="Team">
        <select value={teamId} disabled={loadingTeams}
          onChange={e => {
            const id = e.target.value;
            const name = teams.find(t => t.id === id)?.displayName ?? null;
            // Changing team clears any previously chosen channel.
            onChange({ teamId: id || null, teamName: name, channelId: null, channelName: null });
          }} style={BM_INPUT}>
          <option value="">{loadingTeams ? 'Loading teams…' : 'Select a team…'}</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
        </select>
      </Labeled>
      {!loadingTeams && !error && teams.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
          No Teams found. Make sure you are a member of at least one Team.
        </div>
      )}
      {teamId && (
        <div style={{ marginTop: 8 }}>
          <Labeled label="Channel">
            <select value={channelId} disabled={loadingChannels}
              onChange={e => {
                const id = e.target.value;
                const name = channels.find(c => c.id === id)?.displayName ?? null;
                onChange({ channelId: id || null, channelName: name });
              }} style={BM_INPUT}>
              <option value="">{loadingChannels ? 'Loading channels…' : 'Select a channel…'}</option>
              {channels.map(c => <option key={c.id} value={c.id}>{c.displayName}</option>)}
            </select>
          </Labeled>
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>{error}</div>}
    </>
  );
}

function FieldGroup({ tag, color, children }: { tag: string; color: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16, paddingLeft: 12, borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color, marginBottom: 6 }}>{tag}</div>
      {children}
    </div>
  );
}
function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: 'var(--ink-3)', display: 'block', marginBottom: 2 }}>{label}</label>
      {children}
    </div>
  );
}
const Th = ({ children }: { children?: React.ReactNode }) => (
  <th style={{ padding: '8px 10px', fontWeight: 500, fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '9px 10px', verticalAlign: 'middle' }}>{children}</td>
);
function LinkBtn({ children, onClick, danger }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick}
      style={{ background: 'transparent', border: 'none', padding: 0, color: danger ? '#DC2626' : '#0E7490', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{children}</button>
  );
}
